import FolderIcon from '@mui/icons-material/Folder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SendIcon from '@mui/icons-material/Send';
import { Alert, Box, Chip, TextField, Tooltip } from '@mui/material';
import { useAuth } from '@qwickapps/auth-client';
import { Button, Text } from '@qwickapps/react-framework';
import { useMiddlewareClient, useRealtimeEvents, type Device } from '@qwickforge/react-client';
import { useEffect, useRef, useState } from 'react';
import { config } from '../config/runtime';
import HistoryItem from './chat/HistoryItem';

// Timeline item types from server (keep as raw data, no conversion)
interface TimelinePrompt {
  id: string;
  promptText: string;
  responseText?: string;
  createdAt: string; // ISO UTC from server
  model?: string;
  provider?: string;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
}

interface TimelineExecution {
  id: string;
  promptId: string;
  status: 'pending' | 'running' | 'retrying' | 'failed' | 'completed';
  createdAt: string; // ISO UTC from server
  startedAt?: string; // ISO UTC from server
  completedAt?: string; // ISO UTC from server
  errorMessage?: string;
  agent?: string;
  metadata?: {
    model?: string;
    provider?: string;
    inputTokens?: number;
    outputTokens?: number;
    costUsd?: number;
  };
}

// History data structure: prompt + executions + response
interface HistoryData {
  prompt: TimelinePrompt;
  executions: TimelineExecution[];
  response?: {
    text: string;
    createdAt: string;
  };
}


export interface ChatInterfaceProps {
  projectId?: string;
  sessionId?: string;
  apiUrl?: string;
}

export default function ChatInterface({
  projectId,
  sessionId: propSessionId,
  apiUrl = config.apiUrl,
}: ChatInterfaceProps) {
  const { session } = useAuth();
  const client = useMiddlewareClient();
  const [history, setHistory] = useState<Map<string, HistoryData>>(new Map()); // promptId -> HistoryData
  const [input, setInput] = useState('');
  const [currentSessionId, setCurrentSessionId] = useState<string | undefined>(propSessionId);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>();
  const [projectInfo, setProjectInfo] = useState<{ name: string; currentDir?: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [history]);

  // Load devices on mount only
  // Note: Don't add session to deps to prevent reload on focus when session refreshes
  useEffect(() => {
    if (session?.accessToken) {
      loadDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run on mount

  // Load last session from localStorage on mount
  useEffect(() => {
    if (!propSessionId && !currentSessionId) {
      const lastSessionId = localStorage.getItem('qwickforge_last_session_id');
      if (lastSessionId) {
        console.log('[Chat] Restoring last session:', lastSessionId);
        setCurrentSessionId(lastSessionId);
      }
    }
  }, []);

  const loadDevices = async () => {
    if (!session?.accessToken || !client) return;

    try {
      const data = await client.devices.list();

      // Auto-select device only if there's exactly one device, or if we have a valid last device
      if (data.devices && data.devices.length > 0) {
        const lastDeviceId = localStorage.getItem('qwickforge_last_device_id');

        // If we have a last device ID and it exists in the current devices, use it
        if (lastDeviceId && data.devices.find((d: Device) => d.id === lastDeviceId)) {
          setSelectedDeviceId(lastDeviceId);
          console.log('[Chat] Auto-selected last used device:', lastDeviceId);
        }
        // Only auto-select the first device if there's exactly one device
        else if (data.devices.length === 1) {
          const deviceId = data.devices[0].id;
          setSelectedDeviceId(deviceId);
          localStorage.setItem('qwickforge_last_device_id', deviceId);
          console.log('[Chat] Auto-selected single device:', deviceId);
        }
        // Otherwise, don't auto-select (user must choose from multiple devices)
        else {
          console.log('[Chat] Multiple devices available. User must select one.');
        }
      }
    } catch (error) {
      console.error('[Chat] Failed to load devices:', error);
    }
  };

  // Update currentSessionId when prop changes
  useEffect(() => {
    if (propSessionId) {
      setCurrentSessionId(propSessionId);
    }
  }, [propSessionId]);

  // Load chat history when sessionId changes
  // Note: Don't add session to deps to prevent reload on focus when session refreshes
  useEffect(() => {
    if (currentSessionId && session?.accessToken) {
      loadChatHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId]); // Only reload when session ID changes, not when auth session refreshes

  const loadChatHistory = async () => {
    if (!currentSessionId || !session?.accessToken || !client) return;

    try {
      const data = await client.sessions.getTimeline(currentSessionId);
      console.log('[Chat] Timeline response:', data);

      if (data.timeline && Array.isArray(data.timeline)) {
        const historyMap = new Map<string, HistoryData>();

        // Group timeline items by promptId
        data.timeline.forEach((item: any) => {
          if (item.type === 'prompt') {
            historyMap.set(item.id, {
              prompt: {
                id: item.id,
                promptText: item.promptText || '',
                responseText: item.responseText,
                createdAt: item.createdAt,
                model: item.model,
                provider: item.provider,
                inputTokens: item.inputTokens,
                outputTokens: item.outputTokens,
                costUsd: item.costUsd,
              },
              executions: [],
              response: item.responseText ? {
                text: item.responseText,
                createdAt: item.updatedAt || item.createdAt,
              } : undefined,
            });
          } else if (item.type === 'execution' && item.promptId) {
            const historyData = historyMap.get(item.promptId);
            if (historyData) {
              historyData.executions.push({
                id: item.id,
                promptId: item.promptId,
                status: item.status,
                createdAt: item.createdAt,
                startedAt: item.startedAt,
                completedAt: item.completedAt,
                errorMessage: item.errorMessage,
                agent: item.agent,
                metadata: item.metadata,
              });
            }
          }
        });

        setHistory(historyMap);
        console.log('[Chat] Loaded history:', historyMap.size, 'items');
      }
    } catch (error) {
      console.error('[Chat] Failed to load history:', error);
    }
  };

  const handleRetry = async (promptId: string, executionId: string) => {
    if (!session?.accessToken || !currentSessionId || !client) return;

    try {
      await client.executions.retry(executionId);
      console.log('[Chat] Retrying execution:', executionId);
    } catch (error) {
      console.error('[Chat] Retry error:', error);
    }
  };

  // Load project information when projectId is available
  // Note: Don't add session to deps to prevent reload on focus when session refreshes
  useEffect(() => {
    const loadProjectInfo = async () => {
      if (!projectId || !session?.accessToken || !client) return;

      try {
        const project = await client.projects.get(projectId);
        setProjectInfo({
          name: project.name || 'Unknown Project',
          currentDir: project.currentDir,
        });
        console.log('[Chat] Loaded project info:', project);
      } catch (error) {
        console.error('[Chat] Failed to load project info:', error);
      }
    };

    loadProjectInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]); // Only reload when project ID changes, not when auth session refreshes

  // Connect to realtime events using user_id + JWT token
  const { connected, error: realtimeError } = useRealtimeEvents({
    userId: session?.user?.id,
    accessToken: session?.accessToken,
    apiUrl,
    handlers: {
      onProjectUpdate: (payload) => {
        console.log('[Chat] Project UPDATE:', payload);

        // Update project info if this is the current project
        if (projectId === payload.id) {
          setProjectInfo((prev) => ({
            ...prev,
            name: payload.name || prev?.name || 'Unknown Project',
            currentDir: payload.currentDir,
          }));
        }
      },

      onPromptInsert: (payload) => {
        console.log('[Chat] Prompt INSERT:', payload.id);

        setHistory(prev => {
          const newHistory = new Map(prev);
          const existing = newHistory.get(payload.id);

          // If entry already exists (from optimistic update), merge with server data
          if (existing) {
            console.log('[Chat] Merging INSERT with existing optimistic entry');
            newHistory.set(payload.id, {
              ...existing,
              prompt: {
                id: payload.id,
                promptText: payload.promptText || existing.prompt.promptText,
                responseText: payload.responseText || existing.prompt.responseText,
                createdAt: payload.createdAt || existing.prompt.createdAt,
                model: payload.model,
                provider: payload.provider,
                inputTokens: payload.inputTokens,
                outputTokens: payload.outputTokens,
                costUsd: payload.costUsd,
              },
            });
          } else {
            // New entry from server (not from optimistic update)
            newHistory.set(payload.id, {
              prompt: {
                id: payload.id,
                promptText: payload.promptText || '',
                responseText: payload.responseText,
                createdAt: payload.createdAt,
                model: payload.model,
                provider: payload.provider,
                inputTokens: payload.inputTokens,
                outputTokens: payload.outputTokens,
                costUsd: payload.costUsd,
              },
              executions: [],
            });
          }
          return newHistory;
        });
      },

      onPromptUpdate: (payload) => {
        console.log('[Chat] Prompt UPDATE:', payload.id);

        setHistory(prev => {
          const newHistory = new Map(prev);
          const historyData = newHistory.get(payload.id);

          if (historyData) {
            // Update prompt with metadata and response
            historyData.prompt = {
              ...historyData.prompt,
              responseText: payload.responseText,
              model: payload.model || historyData.prompt.model,
              provider: payload.provider || historyData.prompt.provider,
              inputTokens: payload.inputTokens ?? historyData.prompt.inputTokens,
              outputTokens: payload.outputTokens ?? historyData.prompt.outputTokens,
              costUsd: payload.costUsd ?? historyData.prompt.costUsd,
            };

            // Add or update response if responseText is available
            if (payload.responseText) {
              historyData.response = {
                text: payload.responseText,
                createdAt: payload.updatedAt || payload.createdAt,
              };
            }
          }

          return newHistory;
        });
      },

      onExecutionInsert: (payload) => {
        console.log('[Chat] Execution INSERT:', payload.id, 'for prompt:', payload.promptId);

        if (payload.promptId) {
          setHistory(prev => {
            const newHistory = new Map(prev);
            const historyData = newHistory.get(payload.promptId);

            if (historyData) {
              historyData.executions.push({
                id: payload.id,
                promptId: payload.promptId,
                status: payload.status,
                createdAt: payload.createdAt,
                startedAt: payload.startedAt,
                completedAt: payload.completedAt,
                errorMessage: payload.errorMessage,
                agent: payload.agent,
                metadata: payload.metadata,
              });
            }

            return newHistory;
          });
        }
      },

      onExecutionUpdate: (payload) => {
        console.log('[Chat] Execution UPDATE:', payload.id, 'status:', payload.status, 'startedAt:', payload.startedAt);

        if (payload.promptId) {
          setHistory(prev => {
            const newHistory = new Map(prev);
            const historyData = newHistory.get(payload.promptId);

            if (historyData) {
              // Find and update the execution
              const executionIndex = historyData.executions.findIndex(e => e.id === payload.id);

              if (executionIndex >= 0) {
                // Create new executions array to trigger React re-render
                const newExecutions = [...historyData.executions];
                newExecutions[executionIndex] = {
                  ...historyData.executions[executionIndex],
                  status: payload.status,
                  // Use payload values directly, falling back to existing only if undefined
                  startedAt: payload.startedAt !== undefined ? payload.startedAt : historyData.executions[executionIndex].startedAt,
                  completedAt: payload.completedAt !== undefined ? payload.completedAt : historyData.executions[executionIndex].completedAt,
                  errorMessage: payload.errorMessage !== undefined ? payload.errorMessage : historyData.executions[executionIndex].errorMessage,
                  agent: payload.agent || historyData.executions[executionIndex].agent,
                  metadata: payload.metadata || historyData.executions[executionIndex].metadata,
                };

                // Create new history data object with new executions array
                newHistory.set(payload.promptId, {
                  ...historyData,
                  executions: newExecutions,
                });
              }
            }

            return newHistory;
          });
        }
      },

      onConnected: () => {
        console.log('[Chat] Realtime connected');
      },

      onError: (error) => {
        console.error('[Chat] Realtime error:', error);
      },
    },
  });

  const handleSend = async () => {
    if (!input.trim() || !session?.accessToken || !selectedDeviceId || !client) return;

    const tempId = `temp-${Date.now()}`;
    const promptText = input.trim();

    // Optimistically add to history with temporary ID
    setHistory(prev => {
      const newHistory = new Map(prev);
      newHistory.set(tempId, {
        prompt: {
          id: tempId,
          promptText,
          createdAt: new Date().toISOString(),
        },
        executions: [],
      });
      return newHistory;
    });

    setInput('');

    try {
      // Send prompt to backend using middleware client
      const data = await client.prompts.sendFromWeb({
        session_id: currentSessionId,
        device_id: selectedDeviceId,
        project_id: projectId,
        content: promptText,
        model: 'claude-3-5-sonnet-20241022',
        agent_name: 'assistant', // Default agent for web chat
      });

      console.log('[Chat] Prompt sent:', data.id);

      // Store session ID for future use
      if (data.sessionId && !currentSessionId) {
        setCurrentSessionId(data.sessionId);
        localStorage.setItem('qwickforge_last_session_id', data.sessionId);
      }

      // Update temp entry with real ID but keep it visible
      // The INSERT event will fully update it when it arrives
      setHistory(prev => {
        const newHistory = new Map(prev);
        const tempData = newHistory.get(tempId);

        if (tempData) {
          // Keep the entry but update the ID
          newHistory.delete(tempId);
          newHistory.set(data.id, {
            ...tempData,
            prompt: {
              ...tempData.prompt,
              id: data.id, // Update to real ID
            },
          });
          console.log('[Chat] Updated temp prompt', tempId, 'to real ID', data.id);
        }

        return newHistory;
      });
    } catch (error) {
      console.error('[Chat] Failed to send prompt:', error);

      // Remove temp entry on error
      setHistory(prev => {
        const newHistory = new Map(prev);
        newHistory.delete(tempId);
        return newHistory;
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'var(--theme-surface-variant)',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'var(--theme-outline)',
      overflow: 'hidden'
    }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* Project Info Header */}
        {projectInfo && (
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'var(--theme-surface-variant)',
            }}
          >
            {projectInfo.currentDir && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <FolderIcon sx={{ fontSize: 16, color: 'var(--theme-secondary)' }} />
                <Text variant="caption" style={{ fontFamily: 'monospace' }} customColor="var(--theme-text-secondary)">
                  {projectInfo.currentDir}
                </Text>
                <Tooltip title="Change directory using the CLI: /dir <path>">
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'var(--theme-text-disabled)', ml: 0.5 }} />
                </Tooltip>
              </Box>
            )}
          </Box>
        )}

        {/* Connection Status */}
        {!connected && session?.user?.id && (
          <Alert severity="warning" sx={{ m: 2 }}>
            Not connected to realtime updates. Reconnecting...
          </Alert>
        )}

        {realtimeError && (
          <Alert severity="error" sx={{ m: 2 }}>
            Realtime connection error: {realtimeError.message}
          </Alert>
        )}

        {/* Chat History */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
          }}
        >
          {history.size === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'var(--theme-secondary-dark)',
                textAlign: 'center',
              }}
            >
              <Box sx={{ fontSize: '1.2rem', mb: 1 }}>Welcome to QwickForge</Box>
              <Box sx={{ fontSize: '0.9rem', maxWidth: 400 }}>
                Ask me anything about your code, request features, or get help with your project.
              </Box>
              {connected && (
                <Chip
                  label="Connected to qwickforge"
                  size="small"
                  sx={{
                    mt: 2,
                    backgroundColor: 'var(--theme-success)',
                    color: 'var(--theme-on-primary)'
                  }}
                />
              )}
            </Box>
          )}

          {Array.from(history.values()).map((historyData) => {
            // Get the latest execution (most recent)
            const latestExecution = historyData.executions.length > 0
              ? historyData.executions[historyData.executions.length - 1]
              : undefined;

            return (
              <HistoryItem
                key={historyData.prompt.id}
                promptId={historyData.prompt.id}
                promptText={historyData.prompt.promptText}
                promptCreatedAt={historyData.prompt.createdAt}
                execution={latestExecution}
                response={historyData.response}
                onRetry={handleRetry}
              />
            );
          })}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'var(--theme-outline)',
            bgcolor: 'var(--theme-surface-variant)',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              multiline
              maxRows={4}
              fullWidth
              placeholder="Ask QwickForge anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!session?.accessToken}
              sx={{
                backgroundColor: 'white',
                border: '1px solid var(--theme-border-light)'
              }}
            />
            <Button
              variant="primary"
              onClick={handleSend}
              disabled={!input.trim() || !session?.accessToken}
              icon={<SendIcon sx={{ position: 'relative', left: '7px' }} />}
              sx={{ minWidth: 56, height: 56 }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
